import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UsersRound } from "lucide-react";
import type { ContactGroupSummary } from "@multi-chat/shared";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { cn } from "../../lib/utils.js";
import { contactsBulkBarClass } from "../contacts/contacts-table-presentation.js";
import {
  campaignPageSelectionBanner,
  currentPageSelectionState,
  syncCheckboxIndeterminate,
  toggleCurrentPageSelection,
} from "./campaigns-presentation.js";
import {
  TableWorkspace,
  TableWorkspaceFooter,
  TableWorkspaceScroll,
} from "../../components/common/TableWorkspace.js";
import { CampaignModalContent } from "./CampaignModalContent.js";

const GROUP_PAGE_SIZE = 25;

export function CampaignGroupPickerPanel({
  groups,
  selectedIds,
  onCancel,
  onConfirm,
  onReview,
  onSelectionChange,
  reviewDisabled,
  busy,
  embedded,
}: {
  groups: readonly ContactGroupSummary[];
  selectedIds: readonly number[];
  onCancel: () => void;
  onConfirm: (groupIds: number[]) => Promise<void>;
  onReview?: () => void;
  onSelectionChange?: (groupIds: number[]) => void;
  reviewDisabled?: boolean;
  busy?: boolean;
  embedded?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [uncontrolledIds, setUncontrolledIds] = useState<Set<number>>(() => new Set(selectedIds));

  useEffect(() => {
    if (onSelectionChange) return;
    setUncontrolledIds(new Set(selectedIds));
  }, [onSelectionChange, selectedIds]);

  const draftIds = onSelectionChange ? new Set(selectedIds) : uncontrolledIds;
  const setDraftIds = (updater: Set<number> | ((current: Set<number>) => Set<number>)) => {
    const current = onSelectionChange ? new Set(selectedIds) : uncontrolledIds;
    const next = typeof updater === "function" ? updater(current) : updater;
    if (onSelectionChange) onSelectionChange([...next]);
    else setUncontrolledIds(next);
  };

  const toggle = (id: number) => {
    setDraftIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [page, setPage] = useState(1);
  const total = groups.length;
  const totalPages = Math.max(1, Math.ceil(total / GROUP_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * GROUP_PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(safePage * GROUP_PAGE_SIZE, total);
  const pageGroups = groups.slice((safePage - 1) * GROUP_PAGE_SIZE, safePage * GROUP_PAGE_SIZE);
  const pageItems = pageGroups.map((group) => ({ id: Number(group.id) }));
  const pageSelection = currentPageSelectionState(pageItems, draftIds);
  const selectionBanner = campaignPageSelectionBanner({
    selectedCount: draftIds.size,
    pageCount: pageItems.length,
    pageFullySelected: pageSelection === "all",
    totalCount: total,
  });
  const setSelectPageCheckbox = useCallback(
    (node: HTMLInputElement | null) => syncCheckboxIndeterminate(node, pageSelection),
    [pageSelection],
  );

  useEffect(() => {
    setPage(1);
  }, [groups]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {embedded ? null : (
        <div className={contactsModalHeaderClass}>
          <AlertDialogTitle>{t("campaigns.groups.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("campaigns.groups.description")}</AlertDialogDescription>
        </div>
      )}
      {groups.length === 0 ? (
        <div className="grid justify-items-start gap-1 px-6 py-4" data-testid="campaign-groups-empty">
          <UsersRound size={18} className="text-muted" aria-hidden="true" />
          <p className="m-0 text-[0.875rem] font-medium">{t("campaigns.groups.empty")}</p>
          <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.groups.emptyHelp")}</p>
        </div>
      ) : (
        <>
        {selectionBanner.kind !== "hidden" ? (
          <div
            className={cn(contactsBulkBarClass, "mx-6 mb-2 rounded-lg")}
            role="region"
            data-testid="campaign-group-selection-bar"
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-[0.8125rem] font-semibold" data-testid="campaign-group-selection-label">
                {selectionBanner.kind === "page"
                  ? t("campaigns.groups.pageSelected", { count: selectionBanner.pageCount })
                  : selectionBanner.kind === "all"
                    ? t("campaigns.groups.allSelected", { count: selectionBanner.total })
                    : t("campaigns.groups.partialSelected", {
                        selected: selectionBanner.selected,
                        total: selectionBanner.total,
                      })}
              </span>
              {selectionBanner.kind === "page" || selectionBanner.kind === "partial" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => setDraftIds(new Set(groups.map((group) => Number(group.id))))}
                  data-testid="campaign-group-select-all"
                >
                  {t("campaigns.groups.selectAll", { count: total })}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => setDraftIds(new Set())}
                data-testid="campaign-group-clear"
              >
                {t("campaigns.groups.clear")}
              </Button>
            </div>
          </div>
        ) : null}
        <TableWorkspace className="mx-6 mb-3 rounded-lg border border-line/70 bg-surface">
          <TableWorkspaceScroll>
            <div className="flex items-center gap-2 border-b border-line/60 px-2 py-1.5">
              <label className="inline-grid size-9 cursor-pointer place-items-center">
                <input
                  ref={setSelectPageCheckbox}
                  type="checkbox"
                  aria-label={t("campaigns.groups.selectPage")}
                  checked={pageSelection === "all"}
                  onChange={() => setDraftIds((current) => toggleCurrentPageSelection(pageItems, current))}
                  className="size-4 accent-[var(--color-accent)]"
                />
              </label>
              <span className="text-[0.75rem] text-muted">{t("campaigns.groups.selectPage")}</span>
            </div>
            <ul className="m-0 list-none p-1" data-testid="campaign-group-picker-list">
              {pageGroups.map((group) => {
                const id = Number(group.id);
                const selected = draftIds.has(id);
                return (
                  <li key={id}>
                    <label
                      className={cn(
                        "flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5",
                        selected ? "bg-accent/8" : "hover:bg-surface-muted",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggle(id)}
                        className="size-4 accent-[var(--color-accent)]"
                        aria-label={group.name}
                      />
                      <span className="min-w-0 flex-1 truncate text-start text-[0.8125rem] font-medium">
                        {group.name}
                      </span>
                      <span className="shrink-0 text-[0.75rem] text-muted tabular-nums">
                        {t("campaigns.groups.memberCount", { count: group.memberCount })}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </TableWorkspaceScroll>
          <TableWorkspaceFooter>
            <p className="m-0 text-[0.75rem] text-muted tabular-nums" data-testid="campaign-group-range">
              {t("campaigns.groups.viewing", { start: rangeStart, end: rangeEnd, total })}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-[0.75rem] text-muted" data-testid="campaign-group-selected-count">
                {t("campaigns.groups.selectedCount", { count: draftIds.size })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {t("campaigns.pagination.previous")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                {t("campaigns.pagination.next")}
              </Button>
            </div>
          </TableWorkspaceFooter>
        </TableWorkspace>
        </>
      )}
      {embedded ? null : (
      <AlertDialogActions className={contactsModalFooterClass}>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("campaigns.actions.cancel")}
        </Button>
        {onReview ? (
          <Button type="button" variant="outline" disabled={reviewDisabled} onClick={onReview}>
            {t("campaigns.preview.action")}
          </Button>
        ) : null}
        <Button type="button" disabled={busy} onClick={() => void onConfirm([...draftIds])}>
          {t("campaigns.groups.add", { count: draftIds.size })}
        </Button>
      </AlertDialogActions>
      )}
    </div>
  );
}

export function CampaignGroupPickerDialog({
  open,
  groups,
  selectedIds,
  onOpenChange,
  onConfirm,
  busy,
}: {
  open: boolean;
  groups: readonly ContactGroupSummary[];
  selectedIds: readonly number[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (groupIds: number[]) => Promise<void>;
  busy?: boolean;
}): React.JSX.Element {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent>
        {open ? (
          <CampaignGroupPickerPanel
            groups={groups}
            selectedIds={selectedIds}
            busy={busy}
            onCancel={() => onOpenChange(false)}
            onConfirm={onConfirm}
          />
        ) : null}
      </CampaignModalContent>
    </AlertDialog>
  );
}
